import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, BookmarkPlus, Check, Droplet, FlaskConical } from "@/lib/icons";
import { useLabNotebook } from "@/lib/labNotebook";

// ── Strong acid / strong base titration model ────────────────────────────────
// Acid in flask: volume Va (mL), conc Ca (M)
// Base titrant added: volume Vb (mL), conc Cb (M)
// Net moles of H+ remaining = (Ca·Va − Cb·Vb) / 1000  (mol)
// Total volume V = (Va + Vb) / 1000  (L)
// If excess acid:  [H+] = excess / V   →  pH = −log10([H+])
// If excess base:  [OH-] = excess / V →  pH = 14 − (−log10([OH-]))
// Near equivalence (|net| → 0): pH = 7 (Kw = 1e-14)

function computePH(Ca: number, Va: number, Cb: number, Vb: number): number {
  const molesAcid = (Ca * Va) / 1000;
  const molesBase = (Cb * Vb) / 1000;
  const totalVolL = (Va + Vb) / 1000;
  if (totalVolL <= 0) return 7;

  const net = molesAcid - molesBase; // + means acid excess
  const eps = 1e-9;

  if (Math.abs(net) < eps) return 7;

  if (net > 0) {
    const H = net / totalVolL;
    return -Math.log10(Math.max(H, 1e-14));
  } else {
    const OH = -net / totalVolL;
    const pOH = -Math.log10(Math.max(OH, 1e-14));
    return 14 - pOH;
  }
}

const pHColor = (pH: number) => {
  if (pH < 1) return "hsl(0, 85%, 45%)";
  if (pH < 3) return "hsl(15, 85%, 50%)";
  if (pH < 5) return "hsl(35, 90%, 55%)";
  if (pH < 6.5) return "hsl(55, 85%, 50%)";
  if (pH < 7.5) return "hsl(120, 50%, 50%)";
  if (pH < 9) return "hsl(170, 55%, 45%)";
  if (pH < 11) return "hsl(210, 60%, 50%)";
  if (pH < 13) return "hsl(260, 50%, 45%)";
  return "hsl(280, 50%, 40%)";
};

// Indicator color (phenolphthalein) - colourless < 8.2, pink > 10
const indicatorColor = (pH: number, indicator: "phenolphthalein" | "methyl-orange" | "universal") => {
  if (indicator === "universal") return pHColor(pH);
  if (indicator === "phenolphthalein") {
    if (pH < 8.2) return "hsl(210, 30%, 95%)"; // colourless
    if (pH > 10) return "hsl(330, 80%, 65%)"; // pink
    const t = (pH - 8.2) / (10 - 8.2);
    return `hsl(330, ${30 + 50 * t}%, ${80 - 15 * t}%)`;
  }
  // methyl orange - red < 3.1, yellow > 4.4
  if (pH < 3.1) return "hsl(5, 80%, 55%)";
  if (pH > 4.4) return "hsl(50, 90%, 60%)";
  const t = (pH - 3.1) / (4.4 - 3.1);
  return `hsl(${5 + 45 * t}, 85%, ${55 + 5 * t}%)`;
};

const acids = [
  { id: "hcl", name: "HCl (strong)", formula: "HCl", defaultC: 0.1 },
  { id: "hno3", name: "HNO₃ (strong)", formula: "HNO₃", defaultC: 0.1 },
] as const;

const bases = [
  { id: "naoh", name: "NaOH (strong)", formula: "NaOH", defaultC: 0.1 },
  { id: "koh", name: "KOH (strong)", formula: "KOH", defaultC: 0.1 },
] as const;

const indicators = [
  { id: "phenolphthalein", name: "Phenolphthalein", endpoint: "8.2 – 10.0" },
  { id: "methyl-orange", name: "Methyl orange", endpoint: "3.1 – 4.4" },
  { id: "universal", name: "Universal indicator", endpoint: "full range" },
] as const;

const STEP_ML = 0.5; // mL added per drip tick
const TICK_MS = 120;
const MAX_TITRANT_ML = 50;

export default function Titration() {
  const [acidIdx, setAcidIdx] = useState(0);
  const [baseIdx, setBaseIdx] = useState(0);
  const [indicatorId, setIndicatorId] = useState<typeof indicators[number]["id"]>("phenolphthalein");
  const [Ca, setCa] = useState(0.1);
  const [Cb, setCb] = useState(0.1);
  const [Va] = useState(25); // mL of acid in flask (fixed for simplicity)
  const [Vb, setVb] = useState(0); // mL of base added
  const [running, setRunning] = useState(false);
  const [logged, setLogged] = useState(false);
  const tickRef = useRef<number | null>(null);
  const { addEntry } = useLabNotebook();

  const acid = acids[acidIdx];
  const base = bases[baseIdx];

  const equivalenceVolume = useMemo(() => (Ca * Va) / Cb, [Ca, Va, Cb]);

  const dataPoints = useMemo(() => {
    const pts: { v: number; pH: number }[] = [];
    const stepFine = 0.1;
    for (let v = 0; v <= MAX_TITRANT_ML + 0.001; v += stepFine) {
      pts.push({ v: +v.toFixed(2), pH: computePH(Ca, Va, Cb, v) });
    }
    return pts;
  }, [Ca, Va, Cb]);

  const currentPH = computePH(Ca, Va, Cb, Vb);

  // Drip animation
  useEffect(() => {
    if (!running) {
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = window.setInterval(() => {
      setVb((prev) => {
        const next = +(prev + STEP_ML).toFixed(2);
        if (next >= MAX_TITRANT_ML) {
          setRunning(false);
          return MAX_TITRANT_ML;
        }
        return next;
      });
    }, TICK_MS);
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
  }, [running]);

  const reset = () => {
    setRunning(false);
    setVb(0);
    setLogged(false);
  };

  const logToNotebook = () => {
    addEntry({
      station: "titration",
      title: `Titration: ${base.formula} → ${acid.formula}`,
      equation: `${acid.formula} + ${base.formula} → salt + H₂O`,
      titrationData: {
        acid: acid.formula,
        base: base.formula,
        volumeAcid: Va,
        concAcid: Ca,
        concBase: Cb,
        equivalenceVolume,
        finalVolume: Vb,
        finalPH: currentPH,
      },
      observation: `Endpoint observed near pH ${currentPH.toFixed(2)} after ${Vb.toFixed(2)} mL of ${base.formula} added.`,
    });
    setLogged(true);
  };

  // Curve geometry
  const W = 480;
  const H = 220;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const xScale = (v: number) => padL + (v / MAX_TITRANT_ML) * (W - padL - padR);
  const yScale = (pH: number) => padT + (1 - pH / 14) * (H - padT - padB);
  const path = dataPoints
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.v).toFixed(2)} ${yScale(p.pH).toFixed(2)}`)
    .join(" ");

  // Burette fill (titrant remaining)
  const buretteFill = 1 - Vb / MAX_TITRANT_ML;
  const flaskColor = indicatorColor(currentPH, indicatorId);
  const atEndpoint =
    indicatorId === "phenolphthalein"
      ? currentPH >= 8.2 && currentPH <= 10
      : indicatorId === "methyl-orange"
      ? currentPH >= 3.1 && currentPH <= 4.4
      : Math.abs(currentPH - 7) < 1;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
      {/* Setup panel */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
            <FlaskConical size={14} className="text-primary" aria-hidden="true" />
            Setup
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Strong acid–strong base titration. Adjust setup, then press Start.
          </p>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Acid in flask</label>
          <div className="flex gap-1">
            {acids.map((a, i) => (
              <button
                key={a.id}
                onClick={() => setAcidIdx(i)}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs border transition-colors ${
                  acidIdx === i
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {a.formula}
              </button>
            ))}
          </div>
          <label className="text-[11px] text-muted-foreground mt-2 block">
            Concentration: <span className="font-mono text-foreground">{Ca.toFixed(2)} M</span>
          </label>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.05}
            value={Ca}
            onChange={(e) => setCa(parseFloat(e.target.value))}
            className="w-full accent-primary"
            aria-label="Acid concentration"
          />
          <p className="text-[10px] text-muted-foreground">Volume in flask: {Va} mL (fixed)</p>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Titrant (base)</label>
          <div className="flex gap-1">
            {bases.map((b, i) => (
              <button
                key={b.id}
                onClick={() => setBaseIdx(i)}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs border transition-colors ${
                  baseIdx === i
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {b.formula}
              </button>
            ))}
          </div>
          <label className="text-[11px] text-muted-foreground mt-2 block">
            Concentration: <span className="font-mono text-foreground">{Cb.toFixed(2)} M</span>
          </label>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.05}
            value={Cb}
            onChange={(e) => setCb(parseFloat(e.target.value))}
            className="w-full accent-primary"
            aria-label="Base concentration"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Indicator</label>
          <select
            value={indicatorId}
            onChange={(e) => setIndicatorId(e.target.value as typeof indicatorId)}
            className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {indicators.map((ind) => (
              <option key={ind.id} value={ind.id}>
                {ind.name} ({ind.endpoint})
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-md border border-border bg-muted/40 p-2.5 text-[11px] text-muted-foreground space-y-0.5">
          <p>
            Equivalence at <span className="font-mono text-foreground">{equivalenceVolume.toFixed(2)} mL</span>
          </p>
          <p>Theoretical pH at equivalence: <span className="font-mono text-foreground">7.00</span></p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setRunning((r) => !r)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            aria-label={running ? "Pause titration" : "Start titration"}
          >
            {running ? <Pause size={12} aria-hidden="true" /> : <Play size={12} aria-hidden="true" />}
            {running ? "Pause" : Vb > 0 ? "Resume" : "Start"}
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors"
            aria-label="Reset titration"
          >
            <RotateCcw size={12} aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>

      {/* Apparatus + curve */}
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-gradient-to-b from-card to-muted/30 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-5 items-end">
            {/* Burette + flask SVG */}
            <div className="flex flex-col items-center" aria-hidden="true">
              <svg viewBox="0 0 120 280" className="w-32 h-72">
                {/* Burette */}
                <rect x="52" y="10" width="16" height="160" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" rx="2" />
                {/* Burette fill */}
                <motion.rect
                  x="53.5"
                  width="13"
                  fill="hsl(210, 70%, 60%)"
                  opacity={0.55}
                  initial={false}
                  animate={{ y: 11 + (1 - buretteFill) * 158, height: 158 * buretteFill }}
                  transition={{ duration: 0.2 }}
                />
                {/* Burette tip */}
                <path d="M 56 170 L 64 170 L 60 180 Z" fill="hsl(var(--border))" />
                {/* Drop falling */}
                {running && (
                  <motion.circle
                    cx="60"
                    r="3"
                    fill="hsl(210, 70%, 60%)"
                    opacity={0.7}
                    initial={{ cy: 182 }}
                    animate={{ cy: 215 }}
                    transition={{ duration: TICK_MS / 1000, repeat: Infinity, ease: "easeIn" }}
                  />
                )}
                {/* Flask */}
                <path
                  d="M 45 220 L 45 235 L 25 270 Q 22 278 30 278 L 90 278 Q 98 278 95 270 L 75 235 L 75 220 Z"
                  fill="none"
                  stroke="hsl(var(--border))"
                  strokeWidth="1.5"
                />
                {/* Flask liquid */}
                <motion.path
                  d="M 28 268 Q 22 278 30 278 L 90 278 Q 98 278 92 268 Z"
                  fill={flaskColor}
                  opacity={0.7}
                  animate={{ fill: flaskColor }}
                  transition={{ duration: 0.3 }}
                />
              </svg>
              <p className="text-[10px] text-muted-foreground mt-1">
                {Vb.toFixed(2)} / {MAX_TITRANT_ML} mL
              </p>
            </div>

            {/* Live readouts */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-border bg-card p-2.5">
                  <p className="text-[10px] text-muted-foreground">Volume added</p>
                  <p className="text-lg font-mono font-semibold text-foreground">{Vb.toFixed(2)} mL</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-2.5">
                  <p className="text-[10px] text-muted-foreground">Current pH</p>
                  <p className="text-lg font-mono font-semibold" style={{ color: pHColor(currentPH) }}>
                    {currentPH.toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-2.5">
                  <p className="text-[10px] text-muted-foreground">% to equiv.</p>
                  <p className="text-lg font-mono font-semibold text-foreground">
                    {Math.min(100, (Vb / equivalenceVolume) * 100).toFixed(0)}%
                  </p>
                </div>
              </div>

              {atEndpoint && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="rounded-md border border-primary/30 bg-primary/10 p-2.5 text-xs text-primary flex items-center gap-1.5"
                  role="status"
                >
                  <Droplet size={12} aria-hidden="true" />
                  Indicator at endpoint - stop titrating!
                </motion.div>
              )}

              <button
                onClick={logToNotebook}
                disabled={Vb === 0 || logged}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={logged ? "Saved to notebook" : "Save current reading to notebook"}
              >
                {logged ? <Check size={12} aria-hidden="true" /> : <BookmarkPlus size={12} aria-hidden="true" />}
                {logged ? "Saved to notebook" : "Save reading to notebook"}
              </button>
            </div>
          </div>
        </div>

        {/* Titration curve */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-foreground">Titration curve</h3>
            <p className="text-[11px] text-muted-foreground">pH vs. volume of titrant added</p>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Titration curve plot">
            {/* Axes */}
            <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="hsl(var(--border))" />
            <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="hsl(var(--border))" />

            {/* Y gridlines & labels (pH 0,7,14) */}
            {[0, 7, 14].map((pH) => (
              <g key={pH}>
                <line
                  x1={padL}
                  x2={W - padR}
                  y1={yScale(pH)}
                  y2={yScale(pH)}
                  stroke="hsl(var(--border))"
                  strokeDasharray="3 4"
                  opacity={0.5}
                />
                <text x={padL - 6} y={yScale(pH) + 3} textAnchor="end" fontSize="10" fill="hsl(var(--muted-foreground))">
                  {pH}
                </text>
              </g>
            ))}
            {/* X labels */}
            {[0, 10, 20, 30, 40, 50].map((v) => (
              <text
                key={v}
                x={xScale(v)}
                y={H - padB + 14}
                textAnchor="middle"
                fontSize="10"
                fill="hsl(var(--muted-foreground))"
              >
                {v}
              </text>
            ))}
            <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">
              Volume of {base.formula} added (mL)
            </text>
            <text
              x={10}
              y={H / 2}
              fontSize="10"
              fill="hsl(var(--muted-foreground))"
              transform={`rotate(-90 10 ${H / 2})`}
              textAnchor="middle"
            >
              pH
            </text>

            {/* Equivalence vertical line */}
            <line
              x1={xScale(equivalenceVolume)}
              x2={xScale(equivalenceVolume)}
              y1={padT}
              y2={H - padB}
              stroke="hsl(var(--primary))"
              strokeDasharray="2 4"
              opacity={0.5}
            />
            <text
              x={xScale(equivalenceVolume) + 4}
              y={padT + 10}
              fontSize="9"
              fill="hsl(var(--primary))"
            >
              equiv. {equivalenceVolume.toFixed(1)} mL
            </text>

            {/* Curve */}
            <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />

            {/* Current marker */}
            <circle
              cx={xScale(Vb)}
              cy={yScale(currentPH)}
              r={5}
              fill={pHColor(currentPH)}
              stroke="hsl(var(--background))"
              strokeWidth="2"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
